import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Building,
  Zap,
  Gauge,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  User,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/' },
    { icon: Building, label: 'Rooms', path: '/rooms' },
    { icon: User, label: 'Tenants', path: '/tenants' },
    { icon: Zap, label: 'Electricity Bills', path: '/electricity' },
    { icon: Gauge, label: 'Meter Readings', path: '/meters' },
    { icon: TrendingUp, label: 'Electricity Trends', path: '/electricity-trends' },
    { icon: CreditCard, label: 'Payments', path: '/payments' },
    { icon: AlertTriangle, label: 'Arrears', path: '/arrears' },
    { icon: FileText, label: 'Reports', path: '/reports' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    { icon: Shield, label: 'Security Deposits', path: '/deposits' },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
  <div className={`
  fixed top-0 left-0 z-50 h-screen w-80 bg-white dark:bg-secondary-900 shadow-xl transition-transform duration-300 ease-in-out border-r border-secondary-200 dark:border-secondary-700
    lg:relative lg:translate-x-0 lg:shadow-none lg:z-auto
    ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
  `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">Tenant Bills</h1>
                <p className="text-sm text-secondary-500 dark:text-secondary-300">Management System</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-2 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
            >
              <X className="w-5 h-5 text-secondary-500 dark:text-secondary-300" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-6 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{user?.username}</p>
                <p className="text-sm text-secondary-500 dark:text-secondary-300 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-6 space-y-2">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={() => setIsOpen(false)}
              >
                <item.icon className="w-5 h-5 mr-3 text-secondary-600 dark:text-secondary-300" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-6 border-t border-secondary-200 dark:border-secondary-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-2 py-2 text-sm font-medium rounded-md text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-700 transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
